import React, { ReactNode } from 'react';
import { Grid2, IconButton, Stack, Typography } from '@mui/material';

import { OnboardingStep, onboardingSteps } from '../../../data-access';
import LifestackLogoName from '../../../assets/logo/new_logo_with_name.png';
import './OnboardingWrapper.scss';
import { ArrowBack } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { OnboardingStepper } from '../OnboardingStepper';
import { OnboardingStepBar } from '../OnboardingStepBar';
import { useBreakpoints } from '../../../utils';

export interface OnboardingButton {
  text: string | React.ReactElement;
  onPress: (e?: React.MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
  loading?: boolean;
  variant?: 'contained' | 'outlined' | 'text';
}

interface OnboardingWrapperProps {
  children?: ReactNode;
  currentOnboardingStep: OnboardingStep;
  title: string | ReactNode;
  subTitle?: string | ReactNode;
  buttons?: OnboardingButton[];
  containerStyle?: React.CSSProperties;
  contentContainerStyle?: React.CSSProperties;
  buttonContainerStyle?: React.CSSProperties;
  belowButtons?: ReactNode;
  onBack?: () => void;
  onSkip?: () => void;
  topGroupStyle?: React.CSSProperties;
  hideBackButton?: boolean;
  onSignIn?: () => void;
  showSignIn?: boolean;
}

export const OnboardingWrapper: React.FC<OnboardingWrapperProps> = (props) => {
  const {
    children,
    currentOnboardingStep,
    title,
    subTitle,
    containerStyle,
    contentContainerStyle,
    topGroupStyle,
    buttonContainerStyle,
    belowButtons,
    onBack = () => void 0,
    onSkip = () => void 0,
    buttons = [],
    hideBackButton = false,
    onSignIn = () => void 0,
    showSignIn = false,
  } = props;

  const { isMobile } = useBreakpoints();

  return (
    <div className="onboarding-wrapper">
      <Grid2 className="onboarding-wrapper__mobile-header-container" container alignItems="center">
        <Grid2 size={4}>
          {!currentOnboardingStep.disableToGoBack && !hideBackButton && (
            <IconButton
              className="onboarding-wrapper__mobile-header-container__back-button"
              onClick={onBack}
            >
              <ArrowBack />
            </IconButton>
          )}
        </Grid2>
        <Grid2 size={4}>
          <OnboardingStepBar currentStep={currentOnboardingStep} steps={onboardingSteps} />
        </Grid2>
      </Grid2>
      {currentOnboardingStep.afterPaywall && (
        <OnboardingStepper currentStep={currentOnboardingStep} />
      )}
      <div className="onboarding-wrapper__container" style={containerStyle}>
        <div className="onboarding-wrapper__top-group" style={topGroupStyle}>
          {!isMobile && (
            <div className="onboarding-wrapper__header">
              <img
                src={LifestackLogoName}
                alt="Lifestack Logo"
                className="onboarding-wrapper__header__logo"
              />
            </div>
          )}

          <div className="onboarding-wrapper__title-group">
            <Typography variant="h1" className="onboarding-wrapper__title">
              {title}
            </Typography>
            {!!subTitle && (
              <Typography variant="body1" className="onboarding-wrapper__subtitle">
                {subTitle}
              </Typography>
            )}
          </div>
        </div>

        <div className="onboarding-wrapper__scroll-view" style={contentContainerStyle}>
          {children}
        </div>
        <Stack gap={2} width="100%">
          <div className="onboarding-wrapper__button-container" style={buttonContainerStyle}>
            {buttons.map((button, index) => (
              <LoadingButton
                key={index}
                variant={button.variant || 'contained'}
                onClick={button.onPress}
                disabled={button.disabled || button.loading}
                loading={button.loading}
                className="onboarding-wrapper__button"
                fullWidth
              >
                {button.text}
              </LoadingButton>
            ))}
          </div>
          {!!belowButtons && <div>{belowButtons}</div>}
          {showSignIn && (
            <Typography variant="body1" className="onboarding-wrapper__login-text">
              Already have an account?{' '}
              <span onClick={onSignIn} className="onboarding-wrapper__login-text__link">
                Sign in
              </span>
            </Typography>
          )}
        </Stack>
      </div>
    </div>
  );
};
